import { useLazyQuery } from '@apollo/client';
import { Select } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { message, Divider, Icon } from 'antd';
import { uniqBy } from 'lodash';
import debounce from 'lodash/debounce';
import gql from 'graphql-tag';
import classNames from 'classnames';

/**
 * Renders a city selector component with async data fetching.
 *
 *
 * @param {Object} props - The props object.
 * @param {string} props.value - The selected value.
 * @param {function} props.onChange - The function called when the value changes.
 * @param {function} props.onBlur - The function called when the component loses focus.
 * @param {boolean} props.disabled - Determines if the component is disabled.
 * @param {string} props.placeholder - The placeholder text.
 * @param {Array} props.defaultCitiesList - The default list of cities.
 * @return {JSX.Element} The city selector component.
 */
const AsyncCitySelector = ({
  value,
  onChange,
  onBlur,
  disabled,
  placeholder,
  defaultCitiesList,
  className,
  isMultiple,
  valueField = 'id',
}) => {
  const { t } = useTranslation();
  const [cityList, setCityList] = useState(defaultCitiesList || []);
  const [pagination, setPagination] = useState({ pageNumber: 1, nameLike: null });
  const [queryCities, { loading, data }] = useLazyQuery(QUERY_CITYS, {
    variables: {
      cityPageNumber: pagination.pageNumber,
      nameLike: pagination.nameLike ? pagination.nameLike : null,
      slugs: valueField === 'slug' && value ? [value] : null,
    },
    fetchPolicy: 'cache-and-network',
    onError: e => {
      message.error(e?.toString());
    },
  });

  useEffect(() => {
    queryCities();
  }, []);

  useEffect(() => {
    setCityList(newestCityList =>
      uniqBy(
        [
          ...newestCityList,
          ...(data?.cities?.edges.map(e => ({
            id: e.node.id,
            slug: e.node.slug,
            name: e.node.name,
          })) || []),
        ],
        'id',
      ),
    );
  }, [data]);

  const onSearch = keyword => {
    setPagination({
      pageNumber: 1,
      nameLike: keyword,
    });
    const slugsPayload = valueField === 'slug' && value ? [value] : null;
    queryCities({
      variables: {
        cityPageNumber: pagination.pageNumber,
        nameLike: keyword ? keyword : null,
        // if search keyword exist, don't send selected slugs to avoid can't search.
        slugs: keyword ? null : slugsPayload,
      },
    });
  };

  const debounceOnSearch = debounce(onSearch, 800);

  return (
    <Select
      loading={loading}
      onBlur={onBlur}
      showSearch
      className={classNames(className)}
      optionFilterProp={'title'}
      mode={isMultiple && 'multiple'}
      placeholder={placeholder || t('cms.referral.promotion.modal.city_selector.placeholder')}
      value={value}
      disabled={disabled}
      onSearch={debounceOnSearch}
      allowClear
      onPopupScroll={e => {
        e.persist();
        const target = e.target;
        if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
          if (data?.cities?.pageInfo.hasNextPage) {
            setPagination(currentPagination => ({
              ...currentPagination,
              pageNumber: currentPagination.pageNumber + 1,
            }));
          }
        }
      }}
      dropdownRender={menu => (
        <div>
          {menu}
          {loading && (
            <>
              <Divider style={{ margin: '4px 0' }} />
              <div style={{ padding: '4px 8px' }} onMouseDown={e => e.preventDefault()}>
                <Icon type="loading" /> Loading
              </div>
            </>
          )}
        </div>
      )}
      onChange={(v, option) => {
        onChange(v, option);
      }}
    >
      <For of={cityList} each="data" index="index">
        <Select.Option
          key={data.id}
          title={data.slug}
          value={valueField === 'id' ? data.id : data[valueField]}
        >
          {data.name}
        </Select.Option>
      </For>
    </Select>
  );
};
const QUERY_CITYS = gql`
  query cities($cityPageNumber: Int, $nameLike: String, $slugs: [String]) {
    cities(pageSize: 20, pageNumber: $cityPageNumber, nameLike: $nameLike, slugs: $slugs) {
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        numPages
      }
      edges {
        node {
          id
          name
          slug
        }
      }
    }
  }
`;

export default AsyncCitySelector;
